// Short-lived in-memory cache for lookup lists (states, groups, facilities,
// organisations, master types...) that the module screens re-request on
// every tab switch. Each dev/prod API round trip is ~1s, so re-fetching the
// same dropdown data on every tab made screens feel slow.
//
// - Only URLs the caller marks as lookups are cached; records (audits,
//   registers, reports) always load fresh.
// - Identical requests already in flight share one call.
// - Any write in a module (POST/PUT/DELETE, or a GET that deletes/removes)
//   clears that module's lookups, so an edit is never hidden by the cache.
// - Entries are per signed-in user/client and live for 5 minutes.
const TTL_MS = 5 * 60 * 1000;
const store = new Map(); // key -> { at, promise }

const owner = () => `${localStorage.getItem("user_id")}|${localStorage.getItem("client_id")}`;

export const clearLookups = (scope) => {
  for (const key of store.keys()) {
    if (key.startsWith(`${scope}|`)) store.delete(key);
  }
};

const isWriteLikeGet = (url) => /delete|remove/i.test(url);

// fetcher returns { error, result }. Each caller gets its own copy, so a
// screen that sorts or edits its list can't change what others receive.
export const withLookupCache = (scope, lookupPattern, url, fetcher) => {
  if (isWriteLikeGet(url)) {
    clearLookups(scope);
    return fetcher();
  }
  if (!lookupPattern.test(url)) return fetcher();

  const key = `${scope}|${owner()}|${url}`;
  const hit = store.get(key);
  if (!hit || Date.now() - hit.at > TTL_MS) {
    const promise = fetcher().then((response) => {
      if (response.error) store.delete(key); // don't keep failures
      return response;
    });
    store.set(key, { at: Date.now(), promise });
  }
  return store.get(key).promise.then((response) => JSON.parse(JSON.stringify(response)));
};
